<script setup lang="ts">
import { computed } from "vue";
import VHelpTooltip from "../HelpTooltip/VHelpTooltip.vue";

defineOptions({
  name: "VSettingsSection",
});

/**
 * One block of a settings page: the title (with an optional help question mark) in a fixed label column on the left, the controls on the
 * right. Below the `md` breakpoint the label column stacks above the content. Sections are divided by a line;
 * the last one has none.
 */
const {
  title,
  help = "",
  tone = "default",
  labelWidth = 240,
  spacing = "default",
  headingLevel = 2,
} = defineProps<{
  title: string
  /** Help text behind a question mark after the title; never a line under it. */
  help?: string
  /** `danger` colours the title red (Verwijderen, Gevarenzone). */
  tone?: "default" | "danger"
  /** Width of the label column in px: 240 on most boards, 260 on the guest app Beheren tab. */
  labelWidth?: 240 | 260
  /** `relaxed` uses 20px instead of 16px above and below the section. */
  spacing?: "default" | "relaxed"
  headingLevel?: 2 | 3
}>();

defineSlots<{
  default?: () => unknown
}>();

const LABEL_COLUMNS: Record<240 | 260, string> = {
  240: "md:grid-cols-[240px_minmax(0,1fr)]",
  260: "md:grid-cols-[260px_minmax(0,1fr)]",
};

const sectionClass = computed(() => [
  LABEL_COLUMNS[labelWidth],
  spacing === "relaxed" ? "py-5" : "py-4",
]);
const titleClass = computed(() => (tone === "danger" ? "text-red-900 dark:text-red-400" : "text-(--text)"));
const headingTag = computed(() => `h${headingLevel}`);
</script>

<template>
  <section
    class="grid grid-cols-1 gap-x-8 gap-y-3 border-b border-gray-200 last:border-b-0 dark:border-dark-600"
    :class="sectionClass"
  >
    <div class="flex min-w-0 items-start gap-1.5">
      <component :is="headingTag" class="m-0 text-base font-bold" :class="titleClass">
        {{ title }}
      </component>
      <VHelpTooltip v-if="help" :content="help" :subject="title" />
    </div>
    <div class="min-w-0">
      <slot />
    </div>
  </section>
</template>
